import { FC, useEffect, useMemo, useState } from 'react';
import { List } from 'antd';
import { StyledListItemContent } from 'theme/styled';
import dayjs from 'dayjs';
import {
  ReportFileRow,
  ReportRow,
  fetchReportFilesByPatient,
  fetchReportsByPatient
} from 'services/api/reports';

import { showRestError } from 'utils/showErrorMessage';
import { ReportCard, ReportFileCard } from 'components/shared';

interface IProps {
  id: string;
  setTotal(total: number): void;
}

const PAGE_LIMIT = 9999;

type MergedRow =
  | (ReportRow & { kind: 'report'; sortDate: string })
  | (ReportFileRow & { kind: 'file'; sortDate: string });

const Reports: FC<IProps> = ({ id, setTotal }) => {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [reportsFiles, setReportsFiles] = useState<ReportFileRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchReportsByPatient(id, { page: 1, limit: PAGE_LIMIT }),
      fetchReportFilesByPatient(id, { page: 1, limit: PAGE_LIMIT })
    ])
      .then(([reportsRes, filesRes]) => {
        setReports(reportsRes.data);
        setReportsFiles(filesRes.data);
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  }, [id]);

  const results = useMemo(() => {
    const data: MergedRow[] = [
      ...reports.map(item => ({
        ...item,
        kind: 'report' as const,
        sortDate: item.createdAt || item.dateReport
      })),
      ...reportsFiles.map(item => ({
        ...item,
        kind: 'file' as const,
        sortDate: item.createdAt
      }))
    ];

    data.sort((a, b) =>
      dayjs(a.sortDate).isAfter(dayjs(b.sortDate)) ? -1 : 1
    );

    return data;
  }, [reports, reportsFiles]);

  useEffect(() => {
    setTotal(results.length);
  }, [results.length, setTotal]);

  return (
    <StyledListItemContent>
      <List
        loading={loading}
        dataSource={results}
        renderItem={item => (
          <List.Item className="list-content-item">
            {item.kind === 'report' ? (
              <ReportCard
                id={String(item.id)}
                appointmentId={
                  item.appointmentId ? String(item.appointmentId) : null
                }
                dateReport={item.dateReport}
                report={item.report}
                status={item.status}
              />
            ) : (
              <ReportFileCard
                id={String(item.id)}
                file={item.file}
                note={item.note}
                date={item.createdAt}
              />
            )}
          </List.Item>
        )}
      />
    </StyledListItemContent>
  );
};

export default Reports;
